import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MapPin, Pencil, Trash2, Search, X } from "lucide-react";
import GooglePlacePicker from "@/components/GooglePlacePicker";

const EMPTY = {
  name: "", address: "", city: "", postal_code: "", country: "France",
  lat: null, lng: null, google_place_id: "",
  contact_name: "", contact_phone: "", contact_email: "", notes: "",
  spaces: [],
};

export default function PlacesPage() {
  const [places, setPlaces] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/places"); setPlaces(data || []); }
    catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditingId(p.id); setForm({ ...EMPTY, ...p }); setOpen(true); };

  // Détecte un lieu déjà existant avec le même nom (insensible à la casse
  // et aux espaces). Exclut le lieu actuellement édité pour ne pas
  // s'auto-signaler quand on ré-ouvre une fiche.
  const duplicatePlace = (() => {
    const v = (form.name || "").trim().toLowerCase();
    if (!v) return null;
    return places.find(p => (p.name || "").trim().toLowerCase() === v && p.id !== editingId) || null;
  })();

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom du lieu requis"); return; }
    if (duplicatePlace) {
      toast.error(`Ce lieu existe déjà : « ${duplicatePlace.name} »${duplicatePlace.city ? ` — ${duplicatePlace.city}` : ""}. Choisissez un autre nom ou modifiez la fiche existante.`);
      return;
    }
    try {
      if (editingId) { await api.patch(`/places/${editingId}`, form); toast.success("Lieu modifié"); }
      else { await api.post("/places", form); toast.success("Lieu créé"); }
      setOpen(false); load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const remove = async (p) => {
    if (!confirm(`Supprimer le lieu "${p.name}" ?`)) return;
    try { await api.delete(`/places/${p.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const filtered = places.filter((p) => {
    const v = q.toLowerCase().trim();
    if (!v) return true;
    return (p.name || "").toLowerCase().includes(v)
      || (p.city || "").toLowerCase().includes(v)
      || (p.address || "").toLowerCase().includes(v);
  });

  return (
    <div className="space-y-6" data-testid="places-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Lieux</div>
        <h1 className="font-brand text-4xl sm:text-5xl lg:text-6xl">CARNET DES LIEUX</h1>
        <p className="text-muted-foreground text-sm">Les lieux enregistrés se propagent en autocomplétion sur les événements.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un lieu, une ville…" className="pl-9" data-testid="places-search" />
        </div>
        <Button className="rounded-full bg-primary text-primary-foreground shadow-glow active:scale-95" onClick={openNew} data-testid="add-place-btn">
          <Plus className="h-4 w-4 mr-2" /> Ajouter un lieu
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
                <th className="text-left px-4 py-3">Nom</th>
                <th className="text-left px-4 py-3">Adresse</th>
                <th className="text-left px-4 py-3">Ville</th>
                <th className="text-left px-4 py-3">Espaces</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">Aucun lieu.</td></tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-border fade-up hover:bg-muted" data-testid={`place-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-semibold">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground max-w-[320px] truncate" title={p.address}>{p.address || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{[p.postal_code, p.city].filter(Boolean).join(" ") || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {(p.spaces && p.spaces.length > 0) ? (
                      <div className="flex flex-wrap gap-1">
                        {p.spaces.slice(0, 3).map((s, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30 text-[10px] font-semibold">{s}</span>
                        ))}
                        {p.spaces.length > 3 && <span className="text-[10px]">+{p.spaces.length - 3}</span>}
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {p.contact_name && <div>{p.contact_name}</div>}
                    {p.contact_phone && <div>{p.contact_phone}</div>}
                    {p.contact_email && <div className="truncate max-w-[160px]" title={p.contact_email}>{p.contact_email}</div>}
                    {!p.contact_name && !p.contact_phone && !p.contact_email && "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`place-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => remove(p)} data-testid={`place-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Modifier le lieu" : "Nouveau lieu"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <GooglePlacePicker
              value={{ name: form.name, address: form.address, city: form.city, postal_code: form.postal_code, country: form.country, lat: form.lat, lng: form.lng, google_place_id: form.google_place_id }}
              onPick={(p) => setForm(f => ({ ...f, ...p }))}
              label="Rechercher sur Google Maps (les champs se remplissent automatiquement)"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <div className="md:col-span-2">
                <Label>Nom *</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  data-testid="place-name"
                  className={duplicatePlace ? "border-destructive focus-visible:ring-destructive" : ""}
                />
                {duplicatePlace && (
                  <div
                    className="mt-2 rounded-md border border-destructive/50 bg-destructive/10 text-destructive text-xs px-3 py-2 flex items-start gap-2"
                    data-testid="place-duplicate-warning"
                  >
                    <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <b>Ce lieu existe déjà</b> : « {duplicatePlace.name} »
                      {duplicatePlace.city ? ` — ${duplicatePlace.city}` : ""}
                      {duplicatePlace.address ? ` (${duplicatePlace.address})` : ""}.
                      <button
                        type="button"
                        className="underline ml-1 font-semibold hover:opacity-80"
                        onClick={() => { setOpen(false); setTimeout(() => openEdit(duplicatePlace), 50); }}
                        data-testid="place-duplicate-open-existing"
                      >
                        Ouvrir la fiche existante
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="place-address" /></div>
              <div><Label>Code postal</Label><Input value={form.postal_code} onChange={e => setForm({ ...form, postal_code: e.target.value })} /></div>
              <div><Label>Ville</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>Contact (nom)</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div><Label>Téléphone</Label><Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Email</Label><Input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="md:col-span-2">
                <Label>Espaces / salles du lieu</Label>
                <SpacesEditor spaces={form.spaces || []} onChange={(spaces) => setForm({ ...form, spaces })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Annuler</Button>
            <Button
              className="rounded-full bg-primary text-primary-foreground disabled:opacity-40"
              onClick={save}
              disabled={!!duplicatePlace}
              data-testid="save-place-btn"
            >
              {editingId ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SpacesEditor({ spaces, onChange }) {
  const [text, setText] = useState("");
  const add = () => {
    const v = text.trim();
    if (!v) return;
    if (spaces.some(s => s.toLowerCase() === v.toLowerCase())) { setText(""); return; }
    onChange([...spaces, v]);
    setText("");
  };
  const remove = (i) => onChange(spaces.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Ex : Grande salle, Terrasse, Salle VIP…"
          data-testid="place-space-input"
        />
        <Button type="button" variant="outline" className="rounded-full shrink-0" onClick={add} data-testid="place-add-space-btn">
          <Plus className="h-4 w-4 mr-1" /> Ajouter
        </Button>
      </div>
      {spaces.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {spaces.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/15 text-primary border border-primary/40 text-xs font-semibold" data-testid={`place-space-${i}`}>
              {s}
              <button type="button" onClick={() => remove(i)} className="hover:text-destructive" aria-label={`Supprimer ${s}`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
